/**
 * COP Dashboard - Pathfinder Chat Tab
 * Real-time user-to-user messaging with @mentions, channels, typing indicators,
 * emoji reactions, and markdown-style formatting.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Menu,
  MenuItem,
  Popper,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  FormControl,
  useTheme,
  alpha,
  ClickAwayListener,
} from '@mui/material';
import {
  Send as SendIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Chat as ChatIcon,
  Campaign as AlertMsgIcon,
  KeyboardArrowDown as ScrollDownIcon,
  Add as AddIcon,
  Tag as TagIcon,
  EmojiEmotions as EmojiIcon,
  Info as InfoIcon,
  NotificationsActive as AlertsOnIcon,
  NotificationsOff as AlertsOffIcon,
  PhotoCamera as PhotoCameraIcon,
  VolumeUp as SoundOnIcon,
  VolumeOff as SoundOffIcon,
} from '@mui/icons-material';
import {
  copTeamChatService,
  ChatMessage,
  ChatChannel,
  ChatUser,
  ChatReaction,
} from '../services/copTeamChatService';

// ─── Shared types for parent component ───
export interface TeamChatTabHandle {
  unreadCount: number;
}

interface TeamChatTabProps {
  currentUserEmail?: string;
  currentUserName?: string;
  selectedCourse?: string;
  isVisible?: boolean;
  onUnreadChange?: (count: number) => void;
  onZoomToLocation?: (lat: number, lng: number) => void;
}

// Quick-pick emoji palette
const REACTION_EMOJIS = ['👍', '✅', '⚠️', '🎯', '👀', '🔥'];

// ─── Markdown-style formatting ───
function formatMarkdown(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code style="background:rgba(128,128,128,0.2);padding:1px 4px;border-radius:3px;font-size:12px">$1</code>');
}

// Highlight @mentions in message text
function renderMessageHtml(raw: string, primaryColor: string): string {
  let html = formatMarkdown(raw);
  html = html.replace(
    /@([\w.\-]+(?:@[\w.\-]+)?)/g,
    `<span style="color:${primaryColor};font-weight:600;background:${primaryColor}18;border-radius:3px;padding:0 2px">@$1</span>`
  );
  return html;
}

const ChatImage: React.FC<{ attachmentUrl?: string | null; alt: string }> = ({ attachmentUrl, alt }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!attachmentUrl) return;
    let cancelled = false;
    let url: string | null = null;

    copTeamChatService.getMediaObjectUrl(attachmentUrl)
      .then((nextUrl) => {
        if (cancelled) {
          URL.revokeObjectURL(nextUrl);
          return;
        }
        url = nextUrl;
        setObjectUrl(nextUrl);
      })
      .catch(() => setFailed(true));

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [attachmentUrl]);

  if (!attachmentUrl || failed) return <Typography sx={{ fontSize: 11, opacity: 0.7 }}>Image unavailable</Typography>;
  if (!objectUrl) return <CircularProgress size={18} />;

  return (
    <Box
      component="img"
      src={objectUrl}
      alt={alt}
      sx={{
        display: 'block',
        maxWidth: 280,
        maxHeight: 260,
        borderRadius: 1.5,
        objectFit: 'cover',
      }}
    />
  );
};

const TeamChatTab: React.FC<TeamChatTabProps> = ({
  currentUserEmail,
  currentUserName,
  selectedCourse,
  isVisible = true,
  onUnreadChange,
  onZoomToLocation,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primaryColor = theme.palette.primary.main;

  const colors = useMemo(() => ({
    bg: isDark ? '#1e2028' : '#fafbfc',
    cardBg: isDark ? 'rgba(40, 42, 54, 0.9)' : 'rgba(255, 255, 255, 0.95)',
    ownBubble: isDark ? alpha(primaryColor, 0.25) : alpha(primaryColor, 0.12),
    otherBubble: isDark ? 'rgba(55, 58, 70, 0.9)' : 'rgba(240, 242, 245, 0.95)',
    ownText: isDark ? '#e0e0e0' : '#1a1a2e',
    otherText: isDark ? '#c8c8c8' : '#333',
    inputBg: isDark ? 'rgba(30, 32, 40, 0.95)' : 'rgba(245, 247, 250, 0.95)',
    border: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    timestamp: isDark ? '#757575' : '#999',
    systemBg: isDark ? 'rgba(255,193,7,0.1)' : 'rgba(255,193,7,0.08)',
  }), [isDark, primaryColor]);

  // ─── Channel state ───
  const courseChannel = selectedCourse && selectedCourse !== 'All Courses'
    ? selectedCourse.toLowerCase().replace(/\s+/g, '-') : null;

  const [channels, setChannels] = useState<ChatChannel[]>([]);
  const [activeChannel, setActiveChannel] = useState('general');
  const [newChannelOpen, setNewChannelOpen] = useState(false);
  const [newChannelName, setNewChannelName] = useState('');
  const [creatingChannel, setCreatingChannel] = useState(false);

  // ─── Messages state ───
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');
  const [contextMenu, setContextMenu] = useState<{ anchorEl: HTMLElement; msg: ChatMessage } | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // ─── Typing indicators ───
  const [typingUsers, setTypingUsers] = useState<Record<string, { name: string; expires: number }>>({});
  const lastTypingSentRef = useRef(0);

  // ─── Emoji picker ───
  const [emojiAnchor, setEmojiAnchor] = useState<{ el: HTMLElement; msgId: number } | null>(null);

  useEffect(() => {
    if (!emojiAnchor) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setEmojiAnchor(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [emojiAnchor]);

  // ─── Info dialog ───
  const [infoOpen, setInfoOpen] = useState(false);

  // ─── Alert filter (persisted) ───
  // Default to hidden — the chat fills up quickly with DevicePower /
  // CellularData / etc. status flips, which drown out actual operator
  // messages. Users who want them can flip the toggle; their choice is
  // persisted in localStorage.
  const [showAlerts, setShowAlerts] = useState(() => {
    const stored = localStorage.getItem('cop_chat_show_alerts');
    return stored === null ? false : stored === 'true';
  });

  // ─── New-message sound (persisted) ───
  // Plays a short ping when a user message arrives in any channel from
  // someone other than the current user. System / alert messages stay
  // silent regardless so the toggle and the alerts filter don't
  // conceptually overlap.
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const stored = localStorage.getItem('cop_chat_sound_enabled');
    return stored === null ? true : stored === 'true';
  });
  const audioCtxRef = useRef<AudioContext | null>(null);
  // We need the latest sound preference inside the SSE callback, which
  // captures state from first render. A ref keeps it fresh without
  // re-subscribing the EventSource each time the toggle flips.
  const soundEnabledRef = useRef(soundEnabled);
  useEffect(() => { soundEnabledRef.current = soundEnabled; }, [soundEnabled]);

  const playPing = useCallback(() => {
    // Re-read the latest preference (ref is updated by the effect above).
    if (!soundEnabledRef.current) return;
    try {
      const Ctor: typeof AudioContext | undefined =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctor) return;
      if (!audioCtxRef.current) audioCtxRef.current = new Ctor();
      const ctx = audioCtxRef.current;
      // Browsers may suspend the context until a user gesture. resume()
      // is a no-op if already running and silently fails otherwise.
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
      const now = ctx.currentTime;
      // Two-tone soft "tink": a higher partial decays into a lower one.
      // Sine waves with a steep envelope read as pleasant, not alarmist.
      const playTone = (freq: number, start: number, duration: number, peakGain: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, now + start);
        gain.gain.setValueAtTime(0, now + start);
        gain.gain.linearRampToValueAtTime(peakGain, now + start + 0.012);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + start + duration);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + start);
        osc.stop(now + start + duration + 0.02);
      };
      playTone(1320, 0, 0.18, 0.08);   // E6 chirp
      playTone(880,  0.05, 0.22, 0.06); // A5 settle
    } catch {
      // Audio is optional — never let a sound failure crash the UI.
    }
  }, []);

  // ─── @mention state ───
  const [knownUsers, setKnownUsers] = useState<ChatUser[]>([]);
  const [mentionAnchor, setMentionAnchor] = useState<HTMLElement | null>(null);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionIdx, setMentionIdx] = useState(0);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const isAtBottomRef = useRef(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const optimisticIdRef = useRef(-1);
  const latestIdRef = useRef(0);

  const myEmail = currentUserEmail?.toLowerCase() || '';

  // ─── Notify parent of unread changes ───
  useEffect(() => {
    onUnreadChange?.(unreadCount);
  }, [unreadCount, onUnreadChange]);

  // Reset unread when tab becomes visible
  useEffect(() => {
    if (isVisible) setUnreadCount(0);
  }, [isVisible]);

  // ─── Load channels + users on mount ───
  useEffect(() => {
    copTeamChatService.getChannels().then(setChannels).catch(() => {});
    copTeamChatService.getUsers().then(setKnownUsers).catch(() => {});
  }, []);

  useEffect(() => {
    if (courseChannel) setActiveChannel(courseChannel);
  }, [courseChannel]);

  // ─── Typing indicator cleanup timer ───
  useEffect(() => {
    const interval = setInterval(() => {
      setTypingUsers((prev) => {
        const now = Date.now();
        const expired = Object.keys(prev).filter((k) => now > prev[k].expires);
        if (expired.length === 0) return prev;
        const next = { ...prev };
        expired.forEach((k) => delete next[k]);
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // ─── Scroll helpers ───
  const scrollToBottom = useCallback((smooth = true) => {
    messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
    setUnreadCount(0);
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    isAtBottomRef.current = atBottom;
    setShowScrollDown(!atBottom);
    if (atBottom) setUnreadCount(0);
  }, []);

  // ─── Load message history ───
  const loadMessages = useCallback(async (beforeId?: number) => {
    try {
      if (beforeId) setLoadingMore(true);
      else setLoading(true);
      const res = await copTeamChatService.getMessages(activeChannel, 50, beforeId);
      const sorted = res.messages.sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
      if (beforeId) {
        setMessages((prev) => [...sorted, ...prev]);
      } else {
        setMessages(sorted);
      }
      if (sorted.length > 0) {
        latestIdRef.current = Math.max(latestIdRef.current, sorted[sorted.length - 1].id);
      }
      setHasMore(res.has_more);
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Failed to load messages');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [activeChannel]);

  // ─── SSE connection per channel ───
  useEffect(() => {
    loadMessages();

    const es = copTeamChatService.connectStream(
      activeChannel,
      // new message
      (msg) => {
        latestIdRef.current = Math.max(latestIdRef.current, msg.id);
        // Deduplicate against the optimistic placeholder for our own
        // outgoing message — we only want to ping for messages that are
        // genuinely new to us.
        const isDuplicate = (() => {
          let dup = false;
          setMessages((prev) => {
            const withoutOptimistic = prev.filter(
              (m) => !(m.id < 0 && m.user_email === msg.user_email && m.message === msg.message)
            );
            if (withoutOptimistic.some((m) => m.id === msg.id)) {
              dup = true;
              return withoutOptimistic;
            }
            return [...withoutOptimistic, msg];
          });
          return dup;
        })();
        // Only ping for incoming user messages — never for system/alert
        // rows, never for echoes of our own send, and never when we
        // already had this message id in state (rare double-deliver).
        const isUserMessage = msg.message_type !== 'system' && msg.message_type !== 'alert';
        const isFromSomeoneElse = msg.user_email.toLowerCase() !== myEmail;
        if (!isDuplicate && isUserMessage && isFromSomeoneElse) {
          playPing();
        }
        if (isAtBottomRef.current) {
          setTimeout(() => scrollToBottom(), 50);
        } else if (!isVisible || msg.user_email !== myEmail) {
          setUnreadCount((c) => c + 1);
        }
        setKnownUsers((prev) => {
          if (prev.some((u) => u.email === msg.user_email)) return prev;
          return [...prev, { email: msg.user_email, name: msg.user_name, last_active: msg.created_at }];
        });
        // Clear typing for this user
        setTypingUsers((prev) => {
          if (!(msg.user_email in prev)) return prev;
          const next = { ...prev };
          delete next[msg.user_email];
          return next;
        });
      },
      // edited
      (edited) => {
        setMessages((prev) => prev.map((m) => (m.id === edited.id ? edited : m)));
      },
      // deleted
      (deleted) => {
        setMessages((prev) => prev.filter((m) => m.id !== deleted.id));
      },
      // error
      undefined,
      // typing
      (data) => {
        if (data.user_email === myEmail) return;
        setTypingUsers((prev) => ({
          ...prev,
          [data.user_email]: { name: data.user_name, expires: Date.now() + 4000 },
        }));
      },
      // reaction added
      (data) => {
        setMessages((prev) => prev.map((m) => {
          if (m.id !== data.message_id) return m;
          const existing = m.reactions || [];
          if (existing.some((r) => r.emoji === data.emoji && r.user_email === data.user_email)) return m;
          return { ...m, reactions: [...existing, { emoji: data.emoji, user_email: data.user_email, user_name: data.user_name }] };
        }));
      },
      // reaction removed
      (data) => {
        setMessages((prev) => prev.map((m) => {
          if (m.id !== data.message_id) return m;
          return { ...m, reactions: (m.reactions || []).filter((r) => !(r.emoji === data.emoji && r.user_email === data.user_email)) };
        }));
      },
    );

    eventSourceRef.current = es;

    // Full reconciliation poll: catches new messages, edits, and deletes
    // even when SSE/Redis isn't available (e.g. local development)
    const pollInterval = setInterval(async () => {
      try {
        const res = await copTeamChatService.getMessages(activeChannel, 50);
        const serverMsgs = res.messages.sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );

        setMessages((prev) => {
          const optimistic = prev.filter((m) => m.id < 0);
          const serverIds = new Set(serverMsgs.map((m) => m.id));

          // Build a map of current messages for comparison
          const prevMap = new Map<number, ChatMessage>(
            prev.filter((m) => m.id > 0).map((m) => [m.id, m] as [number, ChatMessage])
          );

          let changed = false;

          // Check for new, edited, or deleted messages
          if (prev.filter((m) => m.id > 0).length !== serverMsgs.length) changed = true;

          if (!changed) {
            for (const sm of serverMsgs) {
              const existing = prevMap.get(sm.id);
              if (!existing) { changed = true; break; }
              if (existing.message !== sm.message || existing.is_edited !== sm.is_edited) { changed = true; break; }
              // Check reactions changed
              const prevReactCount = existing.reactions?.length ?? 0;
              const newReactCount = sm.reactions?.length ?? 0;
              if (prevReactCount !== newReactCount) { changed = true; break; }
            }
          }

          if (!changed) {
            // Check if any local messages were deleted server-side
            for (const pm of prev) {
              if (pm.id > 0 && !serverIds.has(pm.id)) { changed = true; break; }
            }
          }

          if (!changed) return prev;

          // Update latest ID
          if (serverMsgs.length > 0) {
            latestIdRef.current = Math.max(latestIdRef.current, serverMsgs[serverMsgs.length - 1].id);
          }

          const hadNewMessages = serverMsgs.some((m) => !prevMap.has(m.id));
          if (hadNewMessages && isAtBottomRef.current) {
            setTimeout(() => scrollToBottom(), 50);
          }

          return [...serverMsgs, ...optimistic];
        });
      } catch { /* best-effort */ }
    }, 5000);

    return () => {
      es.close();
      eventSourceRef.current = null;
      clearInterval(pollInterval);
    };
  }, [activeChannel, loadMessages, scrollToBottom, myEmail, isVisible]);

  useEffect(() => {
    if (!loading && messages.length > 0) scrollToBottom(false);
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── @mention autocomplete ───
  const mentionCandidates = useMemo(() => {
    if (!mentionQuery) return knownUsers.slice(0, 8);
    const q = mentionQuery.toLowerCase();
    return knownUsers.filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)).slice(0, 8);
  }, [mentionQuery, knownUsers]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const val = e.target.value;
    setInputValue(val);

    // Send typing indicator (throttled to once per 3s)
    const now = Date.now();
    if (now - lastTypingSentRef.current > 3000) {
      lastTypingSentRef.current = now;
      copTeamChatService.sendTyping(activeChannel);
    }

    const cursorPos = e.target.selectionStart ?? val.length;
    const textBefore = val.substring(0, cursorPos);
    const atMatch = textBefore.match(/@([\w.\-]*)$/);
    if (atMatch) {
      setMentionQuery(atMatch[1]);
      setMentionAnchor(e.target as HTMLElement);
      setMentionIdx(0);
    } else {
      setMentionAnchor(null);
    }
  };

  const insertMention = (user: ChatUser) => {
    const cursorPos = inputRef.current?.selectionStart ?? inputValue.length;
    const textBefore = inputValue.substring(0, cursorPos);
    const textAfter = inputValue.substring(cursorPos);
    const atPos = textBefore.lastIndexOf('@');
    if (atPos === -1) return;
    const mention = `@${user.name.replace(/\s+/g, '.')} `;
    const newValue = textBefore.substring(0, atPos) + mention + textAfter;
    setInputValue(newValue);
    setMentionAnchor(null);
    setTimeout(() => {
      inputRef.current?.focus();
      const pos = atPos + mention.length;
      inputRef.current?.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (mentionAnchor && mentionCandidates.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx((i) => Math.min(i + 1, mentionCandidates.length - 1)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIdx((i) => Math.max(i - 1, 0)); return; }
      if (e.key === 'Tab' || e.key === 'Enter') { e.preventDefault(); insertMention(mentionCandidates[mentionIdx]); return; }
      if (e.key === 'Escape') { setMentionAnchor(null); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  // ─── Send / Edit / Delete ───
  const handleSend = async () => {
    const text = inputValue.trim();
    if (!text || sending) return;
    const tempId = optimisticIdRef.current--;
    const optimisticMsg: ChatMessage = {
      id: tempId, channel: activeChannel, user_email: myEmail,
      user_name: currentUserName || myEmail.split('@')[0],
      message: text, message_type: 'text', is_edited: false,
      created_at: new Date().toISOString(), reactions: [],
    };
    setMessages((prev) => [...prev, optimisticMsg]);
    setInputValue('');
    setMentionAnchor(null);
    setTimeout(() => scrollToBottom(), 30);
    setSending(true);
    try {
      const real = await copTeamChatService.sendMessage(text, activeChannel);
      latestIdRef.current = Math.max(latestIdRef.current, real.id);
      setMessages((prev) => prev.map((m) => (m.id === tempId ? real : m)));
    } catch (e: any) {
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      setError(e.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const handleImageSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    const caption = inputValue.trim();
    setUploadingImage(true);
    try {
      const real = await copTeamChatService.sendImageMessage(file, activeChannel, caption);
      latestIdRef.current = Math.max(latestIdRef.current, real.id);
      setMessages((prev) => [...prev, real]);
      setInputValue('');
      setMentionAnchor(null);
      setTimeout(() => scrollToBottom(), 30);
    } catch (err: any) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleEditSave = async () => {
    if (!editingId || !editingText.trim()) return;
    try {
      await copTeamChatService.editMessage(editingId, editingText.trim(), activeChannel);
      setEditingId(null); setEditingText('');
    } catch (e: any) { setError(e.message || 'Failed to edit'); }
  };

  const handleDelete = async (id: number) => {
    try { await copTeamChatService.deleteMessage(id); }
    catch (e: any) { setError(e.message || 'Failed to delete'); }
  };

  const handleLoadMore = () => { if (messages.length > 0 && hasMore) loadMessages(messages[0].id); };

  // ─── Reactions ───
  const handleReaction = async (msgId: number, emoji: string) => {
    const msg = messages.find((m) => m.id === msgId);
    const existing = msg?.reactions?.find((r) => r.emoji === emoji && r.user_email === myEmail);
    try {
      if (existing) {
        await copTeamChatService.removeReaction(msgId, emoji);
      } else {
        await copTeamChatService.addReaction(msgId, emoji);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to react');
    }
    setEmojiAnchor(null);
  };

  // ─── Channel creation ───
  const handleCreateChannel = async () => {
    const name = newChannelName.trim().toLowerCase().replace(/\s+/g, '-');
    if (!name) return;
    setCreatingChannel(true);
    try {
      await copTeamChatService.createChannel(name);
      setNewChannelOpen(false); setNewChannelName('');
      setActiveChannel(name);
      const ch = await copTeamChatService.getChannels();
      setChannels(ch);
    } catch (e: any) { setError(e.message || 'Failed to create channel'); }
    finally { setCreatingChannel(false); }
  };

  // ─── Helpers ───
  const formatTime = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getInitials = (name: string) => {
    const parts = name.split(/[\s@.]+/);
    return (parts[0]?.[0] || '?').toUpperCase() + (parts[1]?.[0] || '').toUpperCase();
  };

  const getAvatarColor = (email: string) => {
    let hash = 0;
    for (let i = 0; i < email.length; i++) hash = email.charCodeAt(i) + ((hash << 5) - hash);
    return `hsl(${Math.abs(hash) % 360}, 55%, ${isDark ? 40 : 45}%)`;
  };

  const shouldShowAvatar = (idx: number, list: ChatMessage[]) => {
    if (idx === 0) return true;
    const prev = list[idx - 1];
    const curr = list[idx];
    if (prev.user_email !== curr.user_email) return true;
    return new Date(curr.created_at).getTime() - new Date(prev.created_at).getTime() > 5 * 60 * 1000;
  };

  const channelOptions = useMemo(() => {
    const set = new Map<string, ChatChannel>();
    set.set('general', { channel: 'general', message_count: 0, last_message_at: null });
    if (courseChannel) set.set(courseChannel, { channel: courseChannel, message_count: 0, last_message_at: null });
    for (const ch of channels) set.set(ch.channel, ch);
    return Array.from(set.values());
  }, [channels, courseChannel]);

  // Typing indicator text
  const typingText = useMemo(() => {
    const names = (Object.values(typingUsers) as { name: string; expires: number }[]).map(
      (u) => u.name.split(/[\s@]/)[0]
    );
    if (names.length === 0) return '';
    if (names.length === 1) return `${names[0]} is typing...`;
    if (names.length === 2) return `${names[0]} and ${names[1]} are typing...`;
    return `${names[0]} and ${names.length - 1} others are typing...`;
  }, [typingUsers]);

  // Filtered messages (hide alerts if toggled off)
  const filteredMessages = useMemo(() => {
    if (showAlerts) return messages;
    return messages.filter((m) => m.message_type !== 'system' && m.message_type !== 'alert');
  }, [messages, showAlerts]);

  // Group reactions for display
  const groupReactions = (reactions?: ChatReaction[]) => {
    if (!reactions?.length) return [];
    const map = new Map<string, string[]>();
    for (const r of reactions) {
      const list = map.get(r.emoji) || [];
      list.push(r.user_name || r.user_email.split('@')[0]);
      map.set(r.emoji, list);
    }
    return Array.from(map.entries()).map(([emoji, users]) => ({
      emoji, users, count: users.length,
      hasOwn: reactions.some((r) => r.emoji === emoji && r.user_email === myEmail),
    }));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.8, borderBottom: `1px solid ${colors.border}` }}>
        <ChatIcon sx={{ color: primaryColor, fontSize: 18 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Pathfinder Chat</Typography>
        <FormControl size="small" sx={{ minWidth: 110, flex: 1 }}>
          <Select
            value={activeChannel} onChange={(e) => setActiveChannel(e.target.value)} variant="outlined"
            sx={{ fontSize: 12, height: 28, '& .MuiSelect-select': { py: 0.3, display: 'flex', alignItems: 'center', gap: 0.5 }, '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border } }}
            renderValue={(val) => <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><TagIcon sx={{ fontSize: 14, color: primaryColor }} /><span>{val}</span></Box>}
          >
            {channelOptions.map((ch) => (
              <MenuItem key={ch.channel} value={ch.channel} sx={{ fontSize: 12 }}>
                <TagIcon sx={{ fontSize: 14, mr: 0.5, color: primaryColor, opacity: 0.6 }} />{ch.channel}
                {ch.message_count > 0 && <Chip label={ch.message_count} size="small" sx={{ ml: 'auto', height: 18, fontSize: 10 }} />}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title="New channel"><IconButton size="small" onClick={() => setNewChannelOpen(true)} sx={{ color: primaryColor }}><AddIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
        <Tooltip title={showAlerts ? 'Hide system alerts' : 'Show system alerts'}>
          <IconButton
            size="small"
            onClick={() => {
              const next = !showAlerts;
              setShowAlerts(next);
              localStorage.setItem('cop_chat_show_alerts', String(next));
            }}
            sx={{ color: showAlerts ? primaryColor : colors.timestamp }}
          >
            {showAlerts ? <AlertsOnIcon sx={{ fontSize: 18 }} /> : <AlertsOffIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Tooltip>
        <Tooltip title={soundEnabled ? 'Mute new-message sound' : 'Play sound on new message'}>
          <IconButton
            size="small"
            onClick={() => {
              const next = !soundEnabled;
              setSoundEnabled(next);
              localStorage.setItem('cop_chat_sound_enabled', String(next));
              // Play a confirmation chirp when enabling — also primes the
              // AudioContext (browsers require a user gesture before the
              // first sound can play).
              if (next) {
                soundEnabledRef.current = true;
                setTimeout(() => playPing(), 0);
              }
            }}
            sx={{ color: soundEnabled ? primaryColor : colors.timestamp }}
          >
            {soundEnabled ? <SoundOnIcon sx={{ fontSize: 18 }} /> : <SoundOffIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Chat help"><IconButton size="small" onClick={() => setInfoOpen(true)} sx={{ color: colors.timestamp }}><InfoIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mx: 1, mt: 0.5, py: 0 }}>{error}</Alert>}

      {/* Messages area */}
      <Box
        ref={scrollContainerRef} onScroll={handleScroll}
        sx={{ flex: 1, overflow: 'auto', px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 0.3, position: 'relative', '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)', borderRadius: 3 } }}
      >
        {hasMore && (
          <Box sx={{ textAlign: 'center', py: 1 }}>
            {loadingMore ? <CircularProgress size={20} /> : <Chip label="Load older messages" size="small" onClick={handleLoadMore} sx={{ cursor: 'pointer', fontSize: 11 }} />}
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : filteredMessages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, opacity: 0.5 }}>
            <ChatIcon sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="body2">
              {messages.length > 0 && !showAlerts
                ? `${messages.length - filteredMessages.length} alert(s) hidden. Toggle alerts to see them.`
                : `No messages in #${activeChannel}. Start the conversation!`}
            </Typography>
          </Box>
        ) : (
          filteredMessages.map((msg, idx) => {
            const isOwn = msg.user_email.toLowerCase() === myEmail;
            const showAvatar = shouldShowAvatar(idx, filteredMessages);
            const reactions = groupReactions(msg.reactions);

            if (msg.message_type === 'system' || msg.message_type === 'alert') {
              const coordMatch = msg.message.match(/(-?\d+\.\d+),\s*(-?\d+\.\d+)/);
              const hasCoords = coordMatch && onZoomToLocation;
              // Pull out the inline ON/OFF state so we can color it
              // semantically (green=ON, red=OFF) without the message text
              // having to ship emoji. Falls through harmlessly if the
              // message doesn't match.
              const stateMatch = msg.message.match(/\b(ON|OFF|GRANTED|DENIED|FAILED|RESTORED)\b/);
              const stateColor = stateMatch
                ? /OFF|DENIED|FAILED/.test(stateMatch[1]) ? '#ef5350' : '#66bb6a'
                : null;
              // Strip leading emoji/symbols (?, X, check, megaphone, etc.)
              // that some upstream messages prepend so the row stays clean.
              const cleanMessage = msg.message.replace(/^[\s\p{Emoji_Presentation}\p{Extended_Pictographic}\u2705\u274C\u26A0\u2728]+/u, '').trim();
              // Heavy base64/encoded blobs (rare error payloads) get
              // ellipsis-truncated so they can't blow out a row's height.
              const looksLikeBlob = cleanMessage.length > 80 && /^[A-Za-z0-9+/=_-]+$/.test(cleanMessage.replace(/\s/g, ''));
              const displayMessage = looksLikeBlob ? `${cleanMessage.slice(0, 60)}…` : cleanMessage;
              const isAlert = msg.message_type === 'alert';

              return (
                <Box
                  key={msg.id}
                  onClick={hasCoords ? () => onZoomToLocation!(parseFloat(coordMatch![1]), parseFloat(coordMatch![2])) : undefined}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1,
                    py: 0.25,
                    borderLeft: `2px solid ${isAlert ? alpha('#ffc107', 0.6) : alpha(colors.timestamp, 0.35)}`,
                    color: colors.timestamp,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    fontSize: 10.5,
                    lineHeight: 1.4,
                    cursor: hasCoords ? 'pointer' : 'default',
                    transition: 'background 0.12s',
                    '&:hover': hasCoords
                      ? { background: alpha(primaryColor, 0.06), color: isDark ? '#bbb' : '#555' }
                      : {},
                  }}
                >
                  {isAlert && (
                    <AlertMsgIcon sx={{ fontSize: 11, color: alpha('#ffc107', 0.85), flexShrink: 0 }} />
                  )}
                  <Box component="span" sx={{ flexShrink: 0, opacity: 0.7, minWidth: 38 }}>
                    {formatTime(msg.created_at)}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {stateMatch ? (
                      <>
                        {displayMessage.replace(stateMatch[1], '')}
                        <Box component="span" sx={{ color: stateColor || undefined, fontWeight: 600 }}>
                          {stateMatch[1]}
                        </Box>
                      </>
                    ) : (
                      displayMessage
                    )}
                  </Box>
                  {hasCoords && (
                    <Box component="span" sx={{ flexShrink: 0, opacity: 0.6, color: primaryColor, fontSize: 9 }}>
                      ⤴
                    </Box>
                  )}
                </Box>
              );
            }

            return (
              <Box key={msg.id} sx={{ display: 'flex', flexDirection: isOwn ? 'row-reverse' : 'row', alignItems: 'flex-end', gap: 0.5, mt: showAvatar ? 0.6 : 0.15 }}>
                <Tooltip title={msg.user_name || msg.user_email} arrow placement={isOwn ? 'right' : 'left'}>
                  <Box sx={{ width: 22, height: 22, minWidth: 22, borderRadius: '50%', bgcolor: getAvatarColor(msg.user_email), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                    {getInitials(msg.user_name || msg.user_email)}
                  </Box>
                </Tooltip>

                <Box sx={{ maxWidth: '78%', position: 'relative', '&:hover .msg-actions': { opacity: 1 } }}>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexDirection: isOwn ? 'row-reverse' : 'row' }}>
                    {editingId === msg.id ? (
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flex: 1 }}>
                        <TextField size="small" value={editingText} onChange={(e) => setEditingText(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleEditSave(); } if (e.key === 'Escape') { setEditingId(null); setEditingText(''); } }}
                          fullWidth autoFocus multiline maxRows={4} sx={{ '& .MuiInputBase-root': { fontSize: 13 } }} />
                        <IconButton size="small" onClick={handleEditSave}><SendIcon fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => { setEditingId(null); setEditingText(''); }}><CloseIcon fontSize="small" /></IconButton>
                      </Box>
                    ) : (
                      <Box>
                        <Box sx={{
                          px: 1, py: 0.5,
                          borderRadius: isOwn ? '10px 10px 3px 10px' : '10px 10px 10px 3px',
                          bgcolor: isOwn ? colors.ownBubble : colors.otherBubble,
                          color: isOwn ? colors.ownText : colors.otherText,
                          border: `1px solid ${isOwn ? alpha(primaryColor, 0.2) : colors.border}`,
                          wordBreak: 'break-word',
                        }}>
                          {msg.message_type === 'image' ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <ChatImage attachmentUrl={msg.attachment_url} alt={msg.attachment_name || msg.message} />
                              {msg.message && msg.message !== 'Image' && (
                                <Typography sx={{ fontSize: 11.5, lineHeight: 1.4, whiteSpace: 'pre-wrap', '& code': { fontFamily: 'monospace', fontSize: 10.5 } }}
                                  dangerouslySetInnerHTML={{ __html: renderMessageHtml(msg.message, primaryColor) }} />
                              )}
                            </Box>
                          ) : (
                            <Typography sx={{ fontSize: 11.5, lineHeight: 1.4, whiteSpace: 'pre-wrap', '& code': { fontFamily: 'monospace', fontSize: 10.5 } }}
                              dangerouslySetInnerHTML={{ __html: renderMessageHtml(msg.message, primaryColor) }} />
                          )}
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.4, mt: 0.2 }}>
                            {msg.is_edited && <Typography sx={{ fontSize: 8, color: colors.timestamp, fontStyle: 'italic' }}>edited</Typography>}
                            <Typography sx={{ fontSize: 8, color: colors.timestamp }}>{formatTime(msg.created_at)}</Typography>
                          </Box>
                        </Box>

                        {reactions.length > 0 && (
                          <Box sx={{ display: 'flex', gap: 0.2, mt: 0.2, flexWrap: 'wrap', justifyContent: isOwn ? 'flex-end' : 'flex-start' }}>
                            {reactions.map((r) => (
                              <Tooltip key={r.emoji} title={r.users.join(', ')} arrow>
                                <Chip
                                  label={`${r.emoji} ${r.count}`}
                                  size="small"
                                  onClick={() => handleReaction(msg.id, r.emoji)}
                                  sx={{
                                    height: 18, fontSize: 10, cursor: 'pointer',
                                    bgcolor: r.hasOwn ? alpha(primaryColor, 0.15) : 'transparent',
                                    border: `1px solid ${r.hasOwn ? alpha(primaryColor, 0.3) : colors.border}`,
                                    '&:hover': { bgcolor: alpha(primaryColor, 0.1) },
                                  }}
                                />
                              </Tooltip>
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Actions: edit/delete + emoji */}
                    {editingId !== msg.id && (
                      <Box className="msg-actions" sx={{ display: 'flex', flexDirection: 'column', opacity: 0, transition: 'opacity 0.2s', gap: 0 }}>
                        {msg.id > 0 && (
                          <IconButton size="small" onClick={(e) => setEmojiAnchor({ el: e.currentTarget, msgId: msg.id })}>
                            <EmojiIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        )}
                        {isOwn && (
                          <IconButton size="small" onClick={(e) => setContextMenu({ anchorEl: e.currentTarget, msg })}>
                            <MoreIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Typing indicator */}
      {typingText && (
        <Box sx={{ px: 2, py: 0.3, borderTop: `1px solid ${colors.border}` }}>
          <Typography sx={{ fontSize: 11, color: colors.timestamp, fontStyle: 'italic' }}>{typingText}</Typography>
        </Box>
      )}

      {/* Scroll-to-bottom */}
      {showScrollDown && (
        <Box sx={{ position: 'relative' }}>
          <IconButton onClick={() => scrollToBottom()} sx={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', bgcolor: alpha(primaryColor, 0.9), color: '#fff', width: 32, height: 32, boxShadow: 3, '&:hover': { bgcolor: primaryColor }, zIndex: 2 }}>
            {unreadCount > 0 ? <Typography sx={{ fontSize: 11, fontWeight: 700 }}>{unreadCount}</Typography> : <ScrollDownIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Box>
      )}

      {/* Input area */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, px: 1.5, py: 1, borderTop: `1px solid ${colors.border}`, bgcolor: colors.inputBg }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={handleImageSelected}
        />
        <Tooltip title="Attach image">
          <span>
            <IconButton
              onClick={() => fileInputRef.current?.click()}
              disabled={sending || uploadingImage}
              sx={{ color: primaryColor, mb: 0.3, '&.Mui-disabled': { opacity: 0.3 } }}
            >
              {uploadingImage ? <CircularProgress size={18} /> : <PhotoCameraIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </span>
        </Tooltip>
        <TextField
          inputRef={inputRef}
          placeholder={`Message #${activeChannel}... (@ to mention, *bold*, \`code\`)`}
          value={inputValue} onChange={handleInputChange} onKeyDown={handleInputKeyDown}
          multiline maxRows={4} fullWidth size="small" disabled={sending || uploadingImage}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, fontSize: 13, bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#fff' } }}
        />
        <IconButton onClick={handleSend} disabled={!inputValue.trim() || sending || uploadingImage}
          sx={{ bgcolor: alpha(primaryColor, 0.15), color: primaryColor, mb: 0.3, '&:hover': { bgcolor: alpha(primaryColor, 0.25) }, '&.Mui-disabled': { opacity: 0.3 } }}>
          {sending ? <CircularProgress size={18} /> : <SendIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Box>

      {/* @mention popper */}
      <Popper open={Boolean(mentionAnchor) && mentionCandidates.length > 0} anchorEl={mentionAnchor} placement="top-start" sx={{ zIndex: 1400 }}>
        <Paper elevation={6} sx={{ maxHeight: 200, overflow: 'auto', width: 260, bgcolor: colors.cardBg, border: `1px solid ${colors.border}` }}>
          <List dense disablePadding>
            {mentionCandidates.map((u, i) => (
              <ListItemButton key={u.email} selected={i === mentionIdx} onClick={() => insertMention(u)} sx={{ py: 0.5, px: 1.5, '&.Mui-selected': { bgcolor: alpha(primaryColor, 0.12) } }}>
                <Box sx={{ width: 24, height: 24, borderRadius: '50%', mr: 1, bgcolor: getAvatarColor(u.email), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                  {getInitials(u.name)}
                </Box>
                <ListItemText primary={u.name} secondary={u.email} primaryTypographyProps={{ fontSize: 12, fontWeight: 500 }} secondaryTypographyProps={{ fontSize: 10, noWrap: true }} />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      </Popper>

      {/* Emoji quick-pick */}
      <Popper open={Boolean(emojiAnchor)} anchorEl={emojiAnchor?.el} placement="top" sx={{ zIndex: 1400 }}>
        <ClickAwayListener onClickAway={() => setEmojiAnchor(null)}>
          <Paper elevation={6} sx={{ display: 'flex', alignItems: 'center', gap: 0.3, p: 0.5, bgcolor: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
            {REACTION_EMOJIS.map((emoji) => (
              <IconButton key={emoji} size="small" onClick={() => emojiAnchor && handleReaction(emojiAnchor.msgId, emoji)}
                sx={{ fontSize: 16, width: 32, height: 32, '&:hover': { bgcolor: alpha(primaryColor, 0.1) } }}>
                {emoji}
              </IconButton>
            ))}
            <Tooltip title="Close">
              <IconButton size="small" onClick={() => setEmojiAnchor(null)} aria-label="Close reaction picker"
                sx={{ width: 28, height: 28, color: 'text.secondary', '&:hover': { bgcolor: alpha(primaryColor, 0.08) } }}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* Context menu */}
      <Menu anchorEl={contextMenu?.anchorEl} open={Boolean(contextMenu)} onClose={() => setContextMenu(null)}
        slotProps={{ paper: { sx: { minWidth: 120, bgcolor: colors.cardBg, border: `1px solid ${colors.border}` } } }}>
        <MenuItem onClick={() => { if (contextMenu) { setEditingId(contextMenu.msg.id); setEditingText(contextMenu.msg.message); } setContextMenu(null); }} sx={{ fontSize: 13 }}>
          <EditIcon sx={{ fontSize: 16, mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => { if (contextMenu) handleDelete(contextMenu.msg.id); setContextMenu(null); }} sx={{ fontSize: 13, color: 'error.main' }}>
          <DeleteIcon sx={{ fontSize: 16, mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* New channel dialog */}
      <Dialog open={newChannelOpen} onClose={() => setNewChannelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, pb: 1 }}>Create Channel</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth size="small" label="Channel name" placeholder="e.g. ops-team, intel-updates"
            value={newChannelName} onChange={(e) => setNewChannelName(e.target.value.replace(/[^a-zA-Z0-9_\- ]/g, ''))}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreateChannel(); }}
            helperText="Letters, numbers, hyphens, underscores only" sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewChannelOpen(false)} size="small">Cancel</Button>
          <Button onClick={handleCreateChannel} variant="contained" size="small" disabled={!newChannelName.trim() || creatingChannel}>
            {creatingChannel ? <CircularProgress size={16} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Help / Info dialog */}
      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 0.5 }}>
          <InfoIcon sx={{ color: primaryColor }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Pathfinder Chat Guide</Typography>
          <IconButton size="small" onClick={() => setInfoOpen(false)} sx={{ ml: 'auto' }}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ '& h4': { fontWeight: 600, mt: 2, mb: 0.5, fontSize: 14, color: primaryColor }, '& p': { fontSize: 13, lineHeight: 1.6, mb: 1, color: isDark ? '#ccc' : '#444' }, '& code': { bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)', px: 0.8, py: 0.2, borderRadius: 0.5, fontSize: 12, fontFamily: 'monospace' } }}>

          <Typography variant="h4" component="h4">Sending Messages</Typography>
          <Typography component="p">
            Type your message and press <code>Enter</code> to send. Use <code>Shift + Enter</code> for a new line. Messages appear instantly for you and are delivered in real-time to all connected team members.
          </Typography>

          <Typography variant="h4" component="h4">@Mentions</Typography>
          <Typography component="p">
            Type <code>@</code> followed by a name to mention someone. A dropdown will appear with matching users &mdash; use arrow keys to navigate and <code>Tab</code> or <code>Enter</code> to select. Mentions are highlighted in messages so they stand out.
          </Typography>

          <Typography variant="h4" component="h4">Text Formatting</Typography>
          <Typography component="p">
            Wrap text in <code>**double asterisks**</code> for <strong>bold</strong>,
            {' '}<code>*single asterisks*</code> for <em>italic</em>,
            and <code>`backticks`</code> for <code>inline code</code>.
          </Typography>

          <Typography variant="h4" component="h4">Channels</Typography>
          <Typography component="p">
            Use the channel dropdown to switch between conversations. The <code>#general</code> channel is always available. When a specific course is selected in the COP filters, its channel appears automatically. Click the <strong>+</strong> button to create a new channel for a specific team, operation, or topic.
          </Typography>

          <Typography variant="h4" component="h4">Emoji Reactions</Typography>
          <Typography component="p">
            Hover over any message to reveal a smiley face icon. Click it to pick from quick reactions: 👍 ✅ ⚠️ 🎯 👀 🔥. Press <code>Escape</code>, click outside the picker, click the close button, or click the smiley again to dismiss without reacting. Click an existing reaction chip to add yours, or click your own reaction again to remove it. Hover a reaction to see who reacted.
          </Typography>

          <Typography variant="h4" component="h4">Editing &amp; Deleting</Typography>
          <Typography component="p">
            Hover over your own message and click the <strong>&#8942;</strong> menu to edit or delete it. Edited messages show an <em>edited</em> label. Deleted messages are removed for everyone. Administrators can delete any message.
          </Typography>

          <Typography variant="h4" component="h4">Typing Indicators</Typography>
          <Typography component="p">
            When another team member is typing, you'll see "<em>Name is typing...</em>" appear below the messages. This updates in real-time and disappears when they stop typing or send their message.
          </Typography>

          <Typography variant="h4" component="h4">Unread Badge</Typography>
          <Typography component="p">
            When you're on a different tab, new messages are counted and shown as a red badge on the Chat tab icon. Switching to the Chat tab clears the count.
          </Typography>

          <Typography variant="h4" component="h4">Auto-Alerts</Typography>
          <Typography component="p">
            The following events are automatically posted to <code>#general</code> as alert messages. Click any alert with coordinates to zoom the map.
          </Typography>
          <Box component="ul" sx={{ fontSize: 13, pl: 2.5, mb: 1, '& li': { mb: 0.5, color: isDark ? '#ccc' : '#444' } }}>
            <li><strong>SOS Alerts</strong> &mdash; Emergency messages from tracked devices with entity, coordinates, and SOS text</li>
            <li><strong>Geofence Enter/Exit</strong> &mdash; When a device enters or exits a geofence boundary</li>
            <li><strong>Device Back Online</strong> &mdash; When a device resumes reporting after 30+ minutes offline</li>
            <li><strong>Device Went Offline</strong> &mdash; When a device stops reporting for 30+ minutes</li>
            <li><strong>Battery Critical</strong> &mdash; When a device battery drops below 15%</li>
          </Box>

          <Typography variant="h4" component="h4">Share Device to Chat</Typography>
          <Typography component="p">
            In the <strong>Devices</strong> tab, hover over any device and click the <strong>Share</strong> chip. This posts the device's entity, location, speed, and battery info to <code>#general</code> so your team can see it instantly.
          </Typography>

          <Typography variant="h4" component="h4">Keyboard Shortcuts</Typography>
          <Box component="table" sx={{ width: '100%', fontSize: 12, mt: 1, borderCollapse: 'collapse', '& td, & th': { py: 0.5, px: 1, borderBottom: `1px solid ${colors.border}`, textAlign: 'left' } }}>
            <thead><tr><th>Action</th><th>Shortcut</th></tr></thead>
            <tbody>
              <tr><td>Send message</td><td><code>Enter</code></td></tr>
              <tr><td>New line</td><td><code>Shift + Enter</code></td></tr>
              <tr><td>Select @mention</td><td><code>Tab</code> or <code>Enter</code></td></tr>
              <tr><td>Navigate @mentions</td><td><code>↑</code> / <code>↓</code></td></tr>
              <tr><td>Dismiss @mentions</td><td><code>Escape</code></td></tr>
              <tr><td>Save edit</td><td><code>Enter</code></td></tr>
              <tr><td>Cancel edit</td><td><code>Escape</code></td></tr>
            </tbody>
          </Box>

        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} variant="contained" size="small">Got it</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamChatTab;
